import React, { useCallback, useRef, useState } from 'react';
import { Input } from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Box } from 'atoms';
import { RCTable } from 'components';
import { getVendorInvoiceTrips, exportVendorInvoiceTrips } from 'store/vendorBillSlice';
import { searchVendorBillTripsV2, exportVendorInvoiceTripsV2 } from 'store/vendorBillSliceV2';
import { getUIVersion } from 'utils/router-static-data';
import { useQueryParams } from 'hooks';
import { ButtonWrapper } from 'components/ButtonWrapper';

const { Search } = Input;

const SearchContainer = styled(Box)`
	background: #fff;
	padding: 16px;
	border-radius: 8px;
	margin-bottom: 16px;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const SearchSection = styled(Box)`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const LtSearchBar = styled(Search)`
	width: 300px;
	.ant-input-search-button {
		background-color: #18405d;
		border-color: #18405d;
	}
`;

const tripsTableColumns = [
	{
		title: 'Trip ID',
		dataIndex: 'trip_id',
		key: 'trip_id',
		width: 120,
	},
	{
		title: 'Trip Date',
		dataIndex: 'reporting_date',
		key: 'reporting_date',
		width: 120,
	},
	{
		title: 'Trip Type',
		dataIndex: 'trip_type',
		key: 'trip_type',
		width: 120,
	},
	{
		title: 'Project Name',
		dataIndex: 'project',
		key: 'project',
		width: 150,
	},
	{
		title: 'Vehicle Type (Vehicle No)',
		dataIndex: 'vehicle_info',
		key: 'vehicle_info',
		width: 200,
		render: (text, record) => (
			<span>
				{record.vehicle_type} - {record.vehicle_number}
			</span>
		),
	},
	{
		title: 'Vendor Rate',
		dataIndex: 'vendor_trip_cost',
		key: 'vendor_trip_cost',
		width: 120,
	},
	{
		title: 'Payable Amount',
		dataIndex: 'vendor_trip_cost',
		key: 'payable_amount',
		width: 140,
	},
	{
		title: 'Actions',
		key: 'actions',
		width: 80,
		fixed: 'right',
		render: (_, record) => {
			const tripId = record.trip_id || record.id;
			if (!tripId) return '-';

			const location = typeof window !== 'undefined' ? window.location : { pathname: '' };
			return (
				<Link
					to={{ pathname: `/trips/${tripId}/overview` }}
					state={{ from: location.pathname }}
					style={{ color: '#1890ff', fontSize: '16px' }}
					title="View Trip Details"
				>
					<EyeOutlined />
				</Link>
			);
		},
	},
];

export default function VendorBillTripsTab({ vendorBillId }) {
	const dispatch = useDispatch();
	const uiVersion = getUIVersion();
	const useV2API = uiVersion === 'new';
	const [paginateConfig, setPaginateConfig] = useQueryParams({
		search_type: useV2API ? 'vendor_bill_trip' : 'vendor_invoice_trips',
		filter_by: {
			id: Number(vendorBillId),
		},
	});
	const [tableData, setTableData] = useState({});
	const tableRefreshRef = useRef();

	const getTripsAction = useV2API ? searchVendorBillTripsV2 : getVendorInvoiceTrips;

	const memoizedDispatchAction = useCallback(
		(params) => {
			const tabParams = {
				...params,
				id: Number(vendorBillId),
			};
			return getTripsAction(tabParams);
		},
		[vendorBillId, getTripsAction]
	);

	const refreshTable = useCallback(() => {
		dispatch(
			getTripsAction({
				...paginateConfig,
				id: Number(vendorBillId),
			})
		);
	}, [dispatch, paginateConfig, vendorBillId, getTripsAction]);

	// Assign refresh function to ref
	React.useEffect(() => {
		tableRefreshRef.current = refreshTable;
	}, [refreshTable]);

	const handleSearch = (value) => {
		setPaginateConfig({
			...paginateConfig,
			query_str: value,
		});
	};

	const handleExport = () => {
		const exportAction = useV2API
			? exportVendorInvoiceTripsV2({ id: Number(vendorBillId) })
			: exportVendorInvoiceTrips({ id: Number(vendorBillId) });
		dispatch(exportAction);
	};

	return (
		<Box>
			<SearchContainer>
				<SearchSection>
					<LtSearchBar
						placeholder="Search trips..."
						enterButton="Search"
						allowClear
						onSearch={handleSearch}
					/>
				</SearchSection>
				<ButtonWrapper onClick={handleExport} icons={<DownloadOutlined />}>
					EXPORT
				</ButtonWrapper>
			</SearchContainer>
			<RCTable
				dispatchAction={memoizedDispatchAction}
				columns={tripsTableColumns}
				togglePaginate={[paginateConfig, setPaginateConfig]}
				tableDatas={[tableData, setTableData]}
				configs={{
					staticPayload: {
						id: Number(vendorBillId),
					},
				}}
			/>
		</Box>
	);
}

VendorBillTripsTab.propTypes = {
	vendorBillId: PropTypes.string.isRequired,
};
